// client/src/components/game_hud/VitalsBlock.jsx
// Итерация 5: блок витальности в левой панели HUD.
//
// Состав:
//   - 2 полоски с перекрытием: здоровье (+wounds), энергия (+fatigue)
//   - 2 иконки-индикатора: голод 🍖, жажда 💧
//   - индикатор "live/stale"
//   - метаданные персонажа (level, in_structure, surface)

import React from 'react';
import { Box, Divider, Stack, Chip, Tooltip } from '@mui/material';
import {
  WifiTethering as LiveIcon,
  WifiTetheringOff as StaleIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import HealthBar from './HealthBar';
import EnergyBar from './EnergyBar';
import NeedsIndicator from './NeedsIndicator';

const VitalsBlock = ({ vitals, characterMeta, isLive = false, isStale = true }) => {
  const { health, energy, hunger, thirst } = vitals || {};

  return (
    <Box
      sx={{
        p: 0.75,
        borderRadius: 1.5,
        bgcolor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.05)',
      }}
    >
      {/* Заголовок */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <StarIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
          <Box
            component="span"
            sx={{ fontSize: '0.6rem', fontWeight: 600, color: 'text.secondary' }}
          >
            Состояние
          </Box>
          <Chip
            label={`Ур. ${characterMeta?.level || 1}`}
            size="small"
            sx={{
              height: 14,
              '& .MuiChip-label': { fontSize: '0.5rem', px: 0.5 },
              bgcolor: 'rgba(59,130,246,0.1)',
              color: '#60a5fa',
              border: '1px solid rgba(59,130,246,0.3)',
            }}
          />
        </Stack>

        <Tooltip title={isLive ? 'Live (WS)' : 'Нет WS-данных'} arrow>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isLive ? (
              <LiveIcon sx={{ fontSize: 12, color: '#22c55e' }} />
            ) : (
              <StaleIcon sx={{ fontSize: 12, color: isStale ? '#f59e0b' : '#6b7280' }} />
            )}
          </Box>
        </Tooltip>
      </Box>

      {/* Полоски здоровья и энергии */}
      <HealthBar
        current={health?.current ?? 100}
        max={health?.max ?? 100}
        wounds={health?.wounds ?? 0}
        isStale={isStale}
      />
      <EnergyBar
        current={energy?.current ?? 100}
        max={energy?.max ?? 100}
        fatigue={energy?.fatigue ?? 0}
        isStale={isStale}
      />

      <Divider sx={{ my: 0.5 }} />

      {/* Иконки голода и жажды */}
      <Stack direction="row" spacing={1.5} justifyContent="flex-start" sx={{ mb: 0.5 }}>
        <NeedsIndicator
          icon="🍖"
          label="Голод"
          current={hunger?.current ?? 0}
          max={hunger?.max ?? 100}
          rate={hunger?.rate ?? 0}
          invert
          isStale={isStale}
        />
        <NeedsIndicator
          icon="💧"
          label="Жажда"
          current={thirst?.current ?? 0}
          max={thirst?.max ?? 100}
          rate={thirst?.rate ?? 0}
          invert
          isStale={isStale}
        />
      </Stack>

      {/* Метаданные: где персонаж */}
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {characterMeta?.in_structure && (
          <Chip
            label="В структуре"
            size="small"
            sx={{ height: 14, '& .MuiChip-label': { fontSize: '0.45rem', px: 0.5 } }}
          />
        )}
        {characterMeta?.surface && (
          <Chip
            label="Поверхность"
            size="small"
            sx={{ height: 14, '& .MuiChip-label': { fontSize: '0.45rem', px: 0.5 } }}
          />
        )}
        {characterMeta?.in_transit && (
          <Chip
            label="В пути"
            size="small"
            sx={{ height: 14, '& .MuiChip-label': { fontSize: '0.45rem', px: 0.5 } }}
          />
        )}
        {characterMeta?.deck_id && (
          <Chip
            label={`Палуба: ${characterMeta.deck_id}`}
            size="small"
            sx={{ height: 14, '& .MuiChip-label': { fontSize: '0.45rem', px: 0.5 } }}
          />
        )}
      </Stack>
    </Box>
  );
};

export default VitalsBlock;
